import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import workAnalyzerService from '../services/workAnalyzerService';
import AccessTokenManager from '../services/accessTokenManager';

interface AuthTokenContextProps {
  tokenSet: boolean;
  isLoading: boolean;
}

const AuthTokenContext = createContext<AuthTokenContextProps>({ tokenSet: false, isLoading: true });

const AuthTokenProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, isLoading: auth0Loading } = useAuth0();
  const [tokenSet, setTokenSet] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false); // New state to track auth check completion
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const setWorkAnalyzerToken = async () => {
      if (isAuthenticated) {
        try {
          const accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: 'https://fxer.ai/api',
              scope: 'read:all',
            },
          });
          AccessTokenManager.setAccessToken(accessToken);
          setTokenSet(true);
        } catch (error) {
          console.error('Error setting token:', error);
        }
      }
      setIsAuthChecked(true); // Indicate that authentication check is done
    };

    if (!auth0Loading) {
      setWorkAnalyzerToken();
    }
  }, [getAccessTokenSilently, isAuthenticated, auth0Loading]);

  useEffect(() => {
    if (isAuthChecked) {
      setIsLoading(false);
    }
  }, [isAuthChecked]);

  return (
    <AuthTokenContext.Provider value={{ tokenSet, isLoading }}>
      {children}
    </AuthTokenContext.Provider>
  );
};

export const useAuthToken = () => useContext(AuthTokenContext);

export default AuthTokenProvider;
